// Sudoku runtime scene. Owns live engine state + view-only state, wires
// InputHandler → engine via SceneCallbacks, paints via SceneRender, drives
// the recorder + win detection. Pause stops the rAF loop but does NOT
// pause the wall clock — `state.startedAt` is the single source of truth.

import { InputHandler } from '../input/InputHandler.js';
import { HintController } from '../hints/HintController.js';
import { LayoutManager } from '../layout/LayoutManager.js';
import { BoardRenderer } from '../render/BoardRenderer.js';
import { NumpadRenderer } from '../render/NumpadRenderer.js';
import { StatusBarRenderer } from '../render/StatusBarRenderer.js';
import { PauseMenu } from '../ui/PauseMenu.js';
import { EndGameModal } from '../ui/EndGameModal.js';
import { isPuzzleComplete } from './SceneActions.js';
import { buildSceneCallbacks } from './SceneCallbacks.js';
import { RafLoop } from './RafLoop.js';
import {
  renderFrame,
  formatStopwatch,
  type SceneRenderStrings,
  type SceneViewState,
} from './SceneRender.js';
import { ERROR_FLASH_MS, HINT_HIGHLIGHT_MS, STOPWATCH_MAX_MS } from '../utils/constants.js';
import { THEMES } from '../settings/themes.js';
import { errorBump, winSuccess } from '../native/haptics.js';
import { t } from '../i18n/I18n.js';
import { announce, confirmDialog } from '@bn-games/ui-shell';
import { buildSettingsRows } from '../ui/settingsRows.js';
import type { Settings } from '../settings/types.js';
import type { SettingsManager } from '../settings/SettingsManager.js';
import type { GameStateData } from '../game/GameState.js';
import type { GameRecorder } from '../history/GameRecorder.js';
import type { HistoryStore } from '../history/HistoryStore.js';
import type { Hint } from '../hints/types.js';
import type { HintHighlight } from '../render/types.js';
import type { NonEmptyDigit } from '../game/types.js';

export type ExitReason = 'completed' | 'quit';

export interface GameSceneOptions {
  readonly canvas: HTMLCanvasElement;
  readonly layoutManager: LayoutManager;
  readonly settingsManager: SettingsManager;
  readonly boardRenderer: BoardRenderer;
  readonly numpadRenderer: NumpadRenderer;
  readonly statusBarRenderer: StatusBarRenderer;
  readonly hintController: HintController;
  readonly gameRecorder: GameRecorder;
  readonly historyStore: HistoryStore;
  readonly onExit: (reason: ExitReason) => void;
  readonly onShare: (state: GameStateData) => void;
  readonly onRestart: () => void;
  readonly now?: () => number;
}

type Timer = ReturnType<typeof setTimeout> | null;

export class GameScene {
  private state: GameStateData | null = null;
  private inputHandler: InputHandler | null = null;
  private pauseMenu: PauseMenu | null = null;
  private endGameModal: EndGameModal | null = null;
  private readonly raf = new RafLoop(() => this.drawFrame());
  private readonly errorFlash = new Set<number>();
  private selectedIndex: number | null = null;
  private pencilMode = false;
  private isPaused = false;
  private hasFinished = false;
  private activeHint: Hint | null = null;
  private hintExpiresAt = 0;
  private errorFlashTimer: Timer = null;
  private winBannerLabel: string | null = null;
  private focusedDigitLatch: NonEmptyDigit | null = null;
  private isDirty = true;
  private lastTimerSecond = -1;
  private readonly onVisibilityChange = (): void => {
    if (document.hidden) {
      this.raf.stop();
    } else if (!this.isPaused && !this.hasFinished) {
      this.isDirty = true;
      this.raf.start();
    }
  };

  constructor(private readonly opts: GameSceneOptions) {}

  mount(state: GameStateData): void {
    this.state = state;
    this.selectedIndex = null;
    this.pencilMode = false;
    this.isPaused = false;
    this.hasFinished = false;
    this.activeHint = null;
    this.winBannerLabel = null;
    this.focusedDigitLatch = null;
    this.isDirty = true;
    this.lastTimerSecond = -1;
    this.errorFlash.clear();
    this.opts.gameRecorder.start(state);
    this.pauseMenu = new PauseMenu({
      onResume: () => this.resume(),
      onShare: () => { if (this.state !== null) this.opts.onShare(this.state); },
      onRestart: () => this.opts.onRestart(),
      onQuit: () => this.opts.onExit('quit'),
      onSolve: () => { void this.confirmAndReveal(); },
      canSolve: () => !this.hasFinished,
      getSettingsRows: () => buildSettingsRows({ ctx: this.settingsCtx() }),
    });
    this.endGameModal = new EndGameModal({
      onPlayAgain: () => this.opts.onRestart(),
      onBackToMenu: () => this.opts.onExit('completed'),
      // Just dismiss the overlay — the canvas behind it is already painted
      // with the revealed grid, so the player can sit with the answer and
      // press Escape (or wait for an explicit return) when they're done.
      onClose: () => undefined,
    });
    this.inputHandler = new InputHandler({
      canvas: this.opts.canvas,
      getLayout: () => this.opts.layoutManager.getConfig(),
      callbacks: buildSceneCallbacks({
        getState: () => this.state,
        isFinished: () => this.hasFinished,
        isPaused: () => this.isPaused,
        getSelectedIndex: () => this.selectedIndex,
        setSelectedIndex: (idx) => {
          this.selectedIndex = idx;
          this.focusedDigitLatch = null;
          this.isDirty = true;
          this.announceSelection(idx);
        },
        getPencilMode: () => this.pencilMode,
        setPencilMode: (next) => { this.pencilMode = next; this.isDirty = true; },
        onDigitApplied: (idx, err) => this.onDigitApplied(idx, err),
        onEngineMutated: () => this.recordSnapshot(),
        onNumpadDigitTap: (d, entered) => this.onNumpadDigitTap(d, entered),
        requestHint: () => this.requestHint(),
        togglePause: () => this.togglePause(),
      }),
    });
    this.raf.start();
    document.addEventListener('visibilitychange', this.onVisibilityChange);
  }

  unmount(): void {
    this.raf.stop();
    document.removeEventListener('visibilitychange', this.onVisibilityChange);
    this.inputHandler?.dispose();
    this.inputHandler = null;
    this.pauseMenu?.close();
    this.pauseMenu = null;
    this.endGameModal?.close();
    this.endGameModal = null;
    this.errorFlashTimer = clearTimer(this.errorFlashTimer);
    this.state = null;
  }

  markDirty(): void {
    this.isDirty = true;
  }

  // Tap → focus. The same handler runs for keyboard and on-canvas numpad
  // input. If the digit was entered, the selected cell now holds it and the
  // selection wins for the highlight — clear the latch so we don't double-
  // count. If not entered (no selection, or a given cell), latch the digit
  // so the cells/pencil marks holding it light up; re-tapping the latched
  // digit toggles it off.
  private onNumpadDigitTap(digit: NonEmptyDigit, entered: boolean): void {
    if (entered) {
      this.focusedDigitLatch = null;
      this.isDirty = true;
      return;
    }
    this.focusedDigitLatch = this.focusedDigitLatch === digit ? null : digit;
    this.isDirty = true;
  }

  private onDigitApplied(cellIndex: number, wasError: boolean): void {
    this.isDirty = true;
    if (wasError && this.opts.settingsManager.get().highlightErrors) {
      this.errorFlash.add(cellIndex);
      this.errorFlashTimer = clearTimer(this.errorFlashTimer);
      this.errorFlashTimer = setTimeout(() => {
        this.errorFlash.clear();
        this.isDirty = true;
      }, ERROR_FLASH_MS);
      announce(t('game.a11y.errorAnnouncement'), { assertive: true });
      errorBump();
    }
    this.recordSnapshot();
    if (this.state !== null && isPuzzleComplete(this.state)) {
      winSuccess();
      this.finishPuzzle();
    }
  }

  private recordSnapshot(): void {
    if (this.state !== null) {
      this.opts.gameRecorder.record(this.state);
      this.isDirty = true;
    }
  }

  private async confirmAndReveal(): Promise<void> {
    const ok = await confirmDialog({
      title: t('pause.solveConfirm.title'),
      message: t('pause.solveConfirm.message'),
      confirmLabel: t('pause.solveConfirm.confirm'),
      cancelLabel: t('pause.solveConfirm.cancel'),
      danger: true,
    });
    if (!ok) return;
    this.revealSolution();
  }

  private revealSolution(): void {
    if (this.state === null || this.hasFinished) return;
    // Stamp the engine grid with the solver's answer so the board reads the
    // win-state directly. Pencil marks get wiped — the cells are committed.
    const state = this.state;
    for (let i = 0; i < state.user.length; i += 1) {
      state.user[i] = state.puzzle.solution[i] ?? 0;
      state.pencil[i]?.clear();
    }
    this.selectedIndex = null;
    this.focusedDigitLatch = null;
    this.finishPuzzle({ forfeited: true });
  }

  private finishPuzzle(opts: { readonly forfeited?: boolean } = {}): void {
    if (this.state === null || this.hasFinished) return;
    this.hasFinished = true;
    this.pauseMenu?.close();
    this.isPaused = false;
    const state = this.state;
    const record = this.opts.gameRecorder.finish(state, opts);
    this.opts.historyStore.add(record);
    // Forfeit suppresses the win banner — the revealed solution speaks for
    // itself, and the EndGameModal carries the "Solution revealed" copy.
    this.winBannerLabel = opts.forfeited === true ? null : t('game.win.banner');
    const timeMs = this.now() - state.startedAt;
    const timeLabel = formatStopwatch(timeMs);
    // Paint one final frame BEFORE stopping the loop so the canvas shows the
    // completed grid (forfeit path: the player wants to see the answer once
    // they dismiss the modal).
    this.isDirty = true;
    this.drawFrame();
    this.raf.stop();
    announce(
      t('game.end.a11yAnnounce', {
        time: timeLabel,
        hints: state.hintsUsed,
        errors: state.errorsMade,
      }),
      { assertive: true },
    );
    this.endGameModal?.open({
      difficulty: state.puzzle.difficulty,
      timeLabel,
      hintsUsed: state.hintsUsed,
      errorsMade: state.errorsMade,
      forfeited: opts.forfeited === true,
    });
  }

  private requestHint(): void {
    if (this.state === null) return;
    const hint = this.opts.hintController.requestHint(this.state);
    if (hint === null) return;
    this.activeHint = hint;
    this.hintExpiresAt = this.now() + HINT_HIGHLIGHT_MS;
    this.isDirty = true;
    this.recordSnapshot();
    const focusCell = hint.highlightedCells[0];
    if (focusCell !== undefined) {
      announce(
        t('game.a11y.hintAnnouncement', {
          technique: techniqueLabel(hint.technique),
          row: Math.floor(focusCell / 9) + 1,
          col: (focusCell % 9) + 1,
        }),
        { assertive: true },
      );
    }
  }

  private announceSelection(index: number | null): void {
    if (index === null || this.state === null) return;
    const row = Math.floor(index / 9) + 1;
    const col = (index % 9) + 1;
    const given = this.state.puzzle.givens[index] ?? 0;
    const user = this.state.user[index] ?? 0;
    const value =
      given !== 0
        ? t('game.a11y.cellGiven', { digit: given })
        : user !== 0
          ? t('game.a11y.cellEntered', { digit: user })
          : t('game.a11y.cellEmpty');
    announce(t('game.a11y.cellAnnouncement', { row, col, value }));
  }

  private togglePause(): void {
    // Once the end-game modal is up, Escape must not stack a pause menu on
    // top of it. The modal owns the post-finish surface — let it handle exit.
    if (this.hasFinished) return;
    if (this.isPaused) this.resume();
    else this.pause();
  }

  private pause(): void {
    this.isPaused = true;
    this.isDirty = true;
    this.raf.stop();
    this.pauseMenu?.open();
  }

  private resume(): void {
    this.isPaused = false;
    this.pauseMenu?.close();
    this.isDirty = true;
    this.raf.start();
  }

  private drawFrame(): void {
    if (this.state === null) return;
    if (this.activeHint !== null && this.now() >= this.hintExpiresAt) {
      this.activeHint = null;
      this.isDirty = true;
    }
    const elapsedMs = this.now() - this.state.startedAt;
    const timerSecond = Math.floor(Math.min(elapsedMs, STOPWATCH_MAX_MS) / 1000);
    if (!this.isDirty && timerSecond === this.lastTimerSecond) return;
    this.isDirty = false;
    this.lastTimerSecond = timerSecond;
    const settings = this.opts.settingsManager.get();
    const ctx = this.opts.canvas.getContext('2d');
    if (ctx === null) throw new Error('GameScene: canvas 2D context unavailable');
    const view: SceneViewState = {
      selectedIndex: this.selectedIndex,
      pencilMode: this.pencilMode,
      errorFlash: this.errorFlash,
      pressedButton: null,
      highlightErrors: settings.highlightErrors,
      winBannerLabel: this.winBannerLabel,
      activeHint: this.buildHintHighlight(),
      focusedDigitLatch: this.focusedDigitLatch,
      paused: this.isPaused,
      pressedPause: false,
    };
    renderFrame({
      ctx, view, state: this.state,
      layout: this.opts.layoutManager.getConfig(),
      theme: THEMES[settings.theme],
      elapsedMs,
      strings: this.renderStrings(),
      board: this.opts.boardRenderer,
      numpad: this.opts.numpadRenderer,
      statusBar: this.opts.statusBarRenderer,
    });
  }

  private renderStrings(): SceneRenderStrings {
    return {
      statusGameLabel: t('game.stats.game'),
      statusErrorsLabel: t('game.stats.errors'),
      statusTimeLabel: t('game.timer.label'),
      difficultyLabels: {
        easy: t('play.difficulty.easy'),
        medium: t('play.difficulty.medium'),
        hard: t('play.difficulty.hard'),
      },
      actionLabels: {
        undo: t('game.undo.button'),
        erase: t('game.erase.button'),
        pencil: t('game.pencil.button'),
        hint: t('game.hint.button'),
      },
    };
  }

  private now(): number {
    return this.opts.now ? this.opts.now() : Date.now();
  }

  private settingsCtx(): { getSettings: () => Settings; updateSettings: (p: Partial<Settings>) => void } {
    return {
      getSettings: () => this.opts.settingsManager.get(),
      updateSettings: (partial) => {
        this.opts.settingsManager.update(partial);
        this.pauseMenu?.refreshSettings();
      },
    };
  }

  private buildHintHighlight(): HintHighlight | null {
    if (this.activeHint === null) return null;
    return {
      cells: new Set(this.activeHint.highlightedCells),
      label: techniqueLabel(this.activeHint.technique),
    };
  }
}

function techniqueLabel(technique: Hint['technique']): string {
  switch (technique) {
    case 'naked-single': return t('hint.technique.naked-single');
    case 'hidden-single': return t('hint.technique.hidden-single');
    case 'naked-pair': return t('hint.technique.naked-pair');
    case 'pointing-pair': return t('hint.technique.pointing-pair');
    case 'box-line': return t('hint.technique.box-line');
    case 'x-wing': return t('hint.technique.x-wing');
  }
}

function clearTimer(timer: Timer): null {
  if (timer !== null) clearTimeout(timer);
  return null;
}
